import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Navigation, User, Package, DollarSign } from 'lucide-react';

const OrdersMapView = ({ orders, onOrderClick }) => {
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [infoWindows, setInfoWindows] = useState([]);

  // Load Google Maps Script
  useEffect(() => {
    const googleMapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    
    if (!googleMapKey) {
      console.error('Google Maps API key not found. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your .env file');
      return;
    }

    // Check if script already loaded
    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapKey}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      initializeMap();
    };
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
    };
    document.head.appendChild(script);

    return () => {
      // Cleanup markers and info windows
      markers.forEach(marker => marker.setMap(null));
      infoWindows.forEach(infoWindow => infoWindow.close());
    };
  }, []);

  // Initialize map
  const initializeMap = () => {
    if (!mapRef.current || !window.google) return;

    const mapInstance = new window.google.maps.Map(mapRef.current, {
      center: { lat: 20.5937, lng: 78.9629 }, // Center of India as default
      zoom: 5,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true,
      zoomControl: true,
    });

    setMap(mapInstance);
  };

  // Update markers when orders change
  useEffect(() => {
    if (!map || !window.google) return;

    // Clear existing markers
    markers.forEach(marker => marker.setMap(null));
    infoWindows.forEach(infoWindow => infoWindow.close());

    // Filter orders with valid coordinates
    const ordersWithLocation = orders.filter(
      order => order.deliveryLocation?.latitude && order.deliveryLocation?.longitude
    );

    if (ordersWithLocation.length === 0) {
      console.log('No orders with valid coordinates found');
      return;
    }

    // Create global function to handle order clicks from info window
    window.handleOrderClick = (orderId, event) => {
      // Stop event propagation to prevent marker from being clicked
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      
      // Close all info windows first
      newInfoWindows.forEach(iw => iw.close());
      
      const order = orders.find(o => o.id === orderId);
      if (order && onOrderClick) {
        onOrderClick(order);
      }
    };

    const newMarkers = [];
    const newInfoWindows = [];
    const bounds = new window.google.maps.LatLngBounds();

    ordersWithLocation.forEach((order, index) => {
      const position = {
        lat: order.deliveryLocation.latitude,
        lng: order.deliveryLocation.longitude,
      };

      // Create custom marker icon based on order status
      const markerIcon = getMarkerIcon(order.status);

      // Create marker
      const marker = new window.google.maps.Marker({
        position,
        map,
        title: `Order: ${order.id}`,
        icon: markerIcon,
        animation: window.google.maps.Animation.DROP,
      });

      // Create info window content with proper escaping
      const safeOrderId = order.id.replace(/'/g, "\\'");
      const safeCustomerName = (order.customerName || '').replace(/'/g, "\\'").replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const safeAddress = (order.customerAddress || '').replace(/'/g, "\\'").replace(/</g, '&lt;').replace(/>/g, '&gt;');
      
      const infoWindowContent = `
        <div style="padding: 14px; max-width: 320px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;">
          <div style="margin-bottom: 10px; display: flex; justify-content: space-between; align-items: center;">
            <strong style="color: #0d6efd; font-size: 15px; font-weight: 600;">Order #${order.id}</strong>
            <span style="background: ${getStatusColor(order.status)}; color: white; padding: 3px 10px; border-radius: 12px; font-size: 11px; font-weight: 500; text-transform: uppercase;">
              ${order.status}
            </span>
          </div>
          <hr style="margin: 10px 0; border: none; border-top: 1px solid #e9ecef;" />
          <div style="font-size: 13px; line-height: 1.7; color: #495057;">
            <div style="margin-bottom: 8px; display: flex; align-items: start;">
              <span style="font-size: 16px; margin-right: 8px;">👤</span>
              <div>
                <strong style="color: #212529;">Customer:</strong><br/>
                <span>${safeCustomerName}</span>
              </div>
            </div>
            <div style="margin-bottom: 8px; display: flex; align-items: center;">
              <span style="font-size: 16px; margin-right: 8px;">📞</span>
              <div>
                <strong style="color: #212529;">Phone:</strong> ${order.customerPhone}
              </div>
            </div>
            <div style="margin-bottom: 8px; display: flex; align-items: start;">
              <span style="font-size: 16px; margin-right: 8px;">📍</span>
              <div style="flex: 1;">
                <strong style="color: #212529;">Address:</strong><br/>
                <span style="color: #6c757d; font-size: 12px; line-height: 1.4;">${safeAddress.substring(0, 80)}${safeAddress.length > 80 ? '...' : ''}</span>
              </div>
            </div>
            <div style="display: flex; justify-content: space-between; margin-top: 12px; padding-top: 10px; border-top: 1px solid #e9ecef;">
              <div style="display: flex; align-items: center;">
                <span style="font-size: 16px; margin-right: 6px;">📦</span>
                <span><strong>${order.items.length}</strong> item(s)</span>
              </div>
              <div style="display: flex; align-items: center;">
                <span style="font-size: 16px; margin-right: 6px;">💰</span>
                <strong style="color: #28a745; font-size: 15px;">₹${order.totalAmount}</strong>
              </div>
            </div>
          </div>
          <button 
            onclick="event.stopPropagation(); event.preventDefault(); window.handleOrderClick('${safeOrderId}', event); return false;"
            style="width: 100%; padding: 10px 16px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 6px; cursor: pointer; font-size: 14px; font-weight: 600; margin-top: 12px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); transition: all 0.2s;"
            onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 4px 8px rgba(0,0,0,0.15)';"
            onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 2px 4px rgba(0,0,0,0.1)';"
          >
            📋 View Full Details
          </button>
        </div>
      `;

      const infoWindow = new window.google.maps.InfoWindow({
        content: infoWindowContent,
      });

      marker.addListener('click', () => {
        // Close all other info windows
        newInfoWindows.forEach(iw => iw.close());
        infoWindow.open(map, marker);
        
        // Pan to marker
        map.panTo(position);
        map.setZoom(15);
      });

      newMarkers.push(marker);
      newInfoWindows.push(infoWindow);
      bounds.extend(position);
    });

    // Fit map to show all markers
    if (ordersWithLocation.length > 0) {
      map.fitBounds(bounds);
      
      // Adjust zoom if only one marker
      if (ordersWithLocation.length === 1) {
        map.setZoom(15);
      }
    }

    setMarkers(newMarkers);
    setInfoWindows(newInfoWindows);

  }, [map, orders, onOrderClick]);

  // Get marker icon based on order status
  const getMarkerIcon = (status) => {
    const colors = {
      pending: '#ffc107',
      accepted: '#17a2b8',
      ready: '#6610f2',
      assigned: '#fd7e14',
      picked_up: '#e83e8c',
      in_transit: '#007bff',
      delivered: '#28a745',
      failed: '#dc3545',
      cancelled: '#6c757d',
    };

    const color = colors[status] || '#6c757d';

    // Create SVG marker with number icon
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 40 50">
        <path d="M20 0C9 0 0 9 0 20c0 15 20 30 20 30s20-15 20-30C40 9 31 0 20 0z" 
              fill="${color}" stroke="#fff" stroke-width="2"/>
        <circle cx="20" cy="20" r="8" fill="#fff"/>
        <text x="20" y="25" font-size="12" font-weight="bold" 
              text-anchor="middle" fill="${color}">📦</text>
      </svg>
    `;

    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new window.google.maps.Size(40, 50),
      anchor: new window.google.maps.Point(20, 50),
    };
  };

  // Get status color for badge
  const getStatusColor = (status) => {
    const colors = {
      pending: '#ffc107',
      accepted: '#17a2b8',
      ready: '#6610f2',
      assigned: '#fd7e14',
      picked_up: '#e83e8c',
      in_transit: '#007bff',
      delivered: '#28a745',
      failed: '#dc3545',
      cancelled: '#6c757d',
    };
    return colors[status] || '#6c757d';
  };

  const ordersWithLocation = orders.filter(
    order => order.deliveryLocation?.latitude && order.deliveryLocation?.longitude
  );

  if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
    return (
      <div className="alert alert-warning m-3">
        <strong>⚠️ Google Maps API Key Missing</strong>
        <p className="mb-0 mt-2">
          Please add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to your <code>.env</code> file to enable map view.
        </p>
      </div>
    );
  }

  return (
    <div className="map-view-container">
      <div className="map-stats-bar">
        <div className="stat-item">
          <MapPin size={16} className="me-2" />
          <span className="stat-label">Orders on Map:</span>
          <span className="stat-value">{ordersWithLocation.length}</span>
        </div>
        <div className="stat-item">
          <Package size={16} className="me-2" />
          <span className="stat-label">Total Orders:</span>
          <span className="stat-value">{orders.length}</span>
        </div>
        {ordersWithLocation.length < orders.length && (
          <div className="stat-item text-warning">
            <Navigation size={16} className="me-2" />
            <span className="stat-label">Without Location:</span>
            <span className="stat-value">{orders.length - ordersWithLocation.length}</span>
          </div>
        )}
      </div>
      
      <div 
        ref={mapRef} 
        className="google-map"
        style={{ 
          width: '100%', 
          height: 'calc(100vh - 250px)',
          minHeight: '500px',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      />

      <style jsx>{`
        .map-view-container {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .map-stats-bar {
          display: flex;
          gap: 2rem;
          padding: 1rem 1.5rem;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          flex-wrap: wrap;
        }

        .stat-item {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
        }

        .stat-label {
          margin-right: 0.5rem;
          opacity: 0.9;
        }

        .stat-value {
          font-weight: 700;
          font-size: 1rem;
        }

        .google-map {
          border: 1px solid #e0e0e0;
        }

        @media (max-width: 768px) {
          .map-stats-bar {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .google-map {
            height: calc(100vh - 300px) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OrdersMapView;

